import { isAxiosError } from 'axios';
import { brand } from '@/config/brand';

/**
 * Friendly, user-facing messages for known auth error codes the backend returns.
 * DataCentral v4 puts the kebab-case code in `error_description`; the auth
 * service uses `message` / `errors[].message`. Keys are the raw backend codes.
 */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  // Contact belongs to a soft-deleted customer — the register/login flow refuses
  // to link the auth account (see datacentral partner auth.register).
  'customer-deleted':
    `This account is linked to a customer profile that is no longer active, so it can’t be used to sign in. Please contact ${brand.shortName} support to restore your account.`,
  'account-already-exists':
    'An account with this email already exists. Please sign in instead.',
  'account-with-this-email-already-exists':
    'An account with this email already exists. Please sign in instead.',
};

/** Raw code + best-effort message pulled from an axios error body. */
function readApiError(e: unknown): { code?: string; message?: string } {
  if (!isAxiosError(e)) return {};
  const body = e.response?.data as
    | {
        error_description?: string; // DataCentral v4 ({ error, error_description })
        error_message?: string; // @mgmt / account service
        message?: string;
        errors?: Array<{ message?: string }>;
      }
    | undefined;
  // DataCentral carries the machine code in the same field it uses for the
  // message, so `code` and `message` are the same kebab-case string here.
  const code = body?.error_description || body?.error_message;
  const message =
    code ||
    body?.message ||
    body?.errors?.[0]?.message ||
    e.message;
  return { code, message };
}

/**
 * Resolve an axios/unknown error into a clear, user-facing message. Known backend
 * codes (e.g. `customer-deleted`) map to a friendly explanation; otherwise the
 * raw backend message is shown, falling back to `fallback`.
 */
export function resolveAuthError(
  e: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  const { code, message } = readApiError(e);
  if (code && AUTH_ERROR_MESSAGES[code]) return AUTH_ERROR_MESSAGES[code];
  return message || fallback;
}

/** The raw kebab-case backend error code (e.g. `account-already-exists`), if any.
 *  Lets callers branch on a specific code instead of matching the message text. */
export function getAuthErrorCode(e: unknown): string | undefined {
  return readApiError(e).code;
}
